// FederatedCredential / IdentityCredential are not in standard TypeScript lib — declare them here

declare class FederatedCredential extends Credential {
  readonly provider: string;
  constructor(data: { id: string; name: string; provider: string; iconURL?: string });
}

// IdentityCredential — the modern FedCM API (Chrome 108+)
declare class IdentityCredential extends Credential {
  readonly token: string;
}

// CredentialRequestOptions extension for modern FedCM identity providers
interface IdentityCredentialRequestOptions {
  providers: Array<{ configURL: string; clientId: string; nonce?: string }>;
}
declare global {
  interface CredentialRequestOptions {
    identity?: IdentityCredentialRequestOptions;
    // legacy — used by FederatedCredential (being deprecated)
    federated?: { providers: string[] };
  }
}

/**
 * FedCM (Federated Credential Management) utilities.
 *
 * FedCM is a browser-native API that replaces third-party cookies for
 * federated login flows. Google Identity Services requires FedCM.
 */

/** Check if the browser supports the Credential Management API */
export function isCredentialManagementSupported(): boolean {
  return typeof window !== "undefined" && "credentials" in navigator;
}

/** Check if the browser supports the modern IdentityCredential FedCM API (Chrome 108+) */
export function isIdentityCredentialSupported(): boolean {
  return isCredentialManagementSupported() && "IdentityCredential" in window;
}

export interface SilentCredentialResult {
  /** Credential ID — email for FederatedCredential, opaque for IdentityCredential */
  id: string;
  /** The credential type */
  type: "federated" | "identity";
  /** Google ID token — present only for IdentityCredential */
  token?: string;
}

/**
 * Attempt silent credential retrieval via the Credential Management API.
 *
 * Tries the modern IdentityCredential API first (Chrome 108+), which returns
 * a Google ID token suitable for direct sign-in. Falls back to the legacy
 * FederatedCredential API for older browsers.
 *
 * Returns null if no stored credential is available or the API isn't supported.
 */
export async function getSilentCredential(): Promise<SilentCredentialResult | null> {
  if (!isCredentialManagementSupported()) return null;

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // Modern path: IdentityCredential (Chrome 108+, FedCM native)
  if (isIdentityCredentialSupported() && clientId) {
    try {
      const credential = await navigator.credentials.get({
        identity: {
          providers: [
            {
              configURL: "https://accounts.google.com/.well-known/web-identity",
              clientId,
            },
          ],
        },
        mediation: "silent",
      });

      if (credential) {
        return {
          id: credential.id,
          type: "identity",
          token: (credential as IdentityCredential).token,
        };
      }
    } catch {
      // Silent failure — no stored credential or user cancelled
    }
  }

  // Legacy path: FederatedCredential (deprecated but widely supported)
  if ("FederatedCredential" in window) {
    try {
      const credential = await navigator.credentials.get({
        federated: {
          providers: ["https://accounts.google.com"],
        },
        mediation: "silent",
      });

      if (credential) {
        return {
          id: credential.id,
          type: "federated",
        };
      }
    } catch {
      // Silent failure
    }
  }

  return null;
}

/**
 * Store a federated credential after successful sign-in.
 * Enables future silent credential retrieval via the legacy FederatedCredential API.
 * (IdentityCredential storage is managed by the browser automatically.)
 */
export async function storeFederatedCredential(
  id: string,
  name: string,
  iconURL?: string
): Promise<void> {
  if (!isCredentialManagementSupported()) return;
  if (!("FederatedCredential" in window)) return;

  try {
    const credential = new FederatedCredential({
      id,
      name,
      provider: "https://accounts.google.com",
      iconURL: iconURL ?? undefined,
    });

    await navigator.credentials.store(credential);
  } catch {
    // Non-critical — credential storage failed
  }
}
